import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BarChart3, TrendingUp, Users, Sparkles, Target, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { useBusinessContent } from '../contexts/BusinessContentContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Analytics = () => {
  // Get business content for metric labels
  const { getMetricLabels, getBusinessTypeName, businessType } = useBusinessContent();
  const metricLabels = getMetricLabels();
  
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${BACKEND_URL}/api/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnalytics(res.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-emerald-300/70">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const leadStatusData = analytics?.lead_status_breakdown?.map((item) => ({
    name: item._id || 'Unknown',
    value: item.count,
  })) || [];

  // Emerald-themed colors
  const COLORS = ['#10b981', '#0a5c44', '#34d399', '#059669', '#6ee7b7'];

  const monthlyData = [
    { month: 'Jan', leads: 45, generations: 120 },
    { month: 'Feb', leads: 52, generations: 145 },
    { month: 'Mar', leads: 68, generations: 189 },
    { month: 'Apr', leads: 85, generations: 220 },
    { month: 'May', leads: analytics?.total_leads || 100, generations: analytics?.total_generations || 250 },
  ];

  // Custom tooltip style
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#052e22] border border-emerald-800/50 rounded-lg px-4 py-3 shadow-lg">
          <p className="text-white font-medium mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div data-testid="analytics-page" className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">{getBusinessTypeName()} Analytics</h1>
        <p className="text-emerald-300/70">Track your performance and insights</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card data-testid="metric-total-leads" className="bg-[#052e22]/50 border-emerald-800/30">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 bg-emerald-500/20 rounded-lg">
                <Users className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="flex items-center text-emerald-400 text-sm">
                <ArrowUpRight className="w-4 h-4 mr-1" />
                +12%
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1">{analytics?.total_leads || 0}</div>
            <p className="text-sm text-emerald-300/70">{metricLabels?.leads || 'Total Leads'}</p>
          </CardContent>
        </Card>

        <Card data-testid="metric-ai-generations" className="bg-[#052e22]/50 border-emerald-800/30">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 bg-emerald-500/20 rounded-lg">
                <Sparkles className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="flex items-center text-emerald-400 text-sm">
                <ArrowUpRight className="w-4 h-4 mr-1" />
                +23%
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1">{analytics?.total_generations || 0}</div>
            <p className="text-sm text-emerald-300/70">AI Generations</p>
          </CardContent>
        </Card>

        <Card data-testid="metric-conversion-rate" className="bg-[#052e22]/50 border-emerald-800/30">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 bg-emerald-500/20 rounded-lg">
                <Target className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="flex items-center text-emerald-400 text-sm">
                <ArrowUpRight className="w-4 h-4 mr-1" />
                +5%
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1">34%</div>
            <p className="text-sm text-emerald-300/70">{metricLabels?.conversions || 'Conversion Rate'}</p>
          </CardContent>
        </Card>

        <Card data-testid="metric-avg-lead-score" className="bg-[#052e22]/50 border-emerald-800/30">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 bg-emerald-500/20 rounded-lg">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="flex items-center text-emerald-400 text-sm">
                <ArrowUpRight className="w-4 h-4 mr-1" />
                +8
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1">67/100</div>
            <p className="text-sm text-emerald-300/70">Avg Lead Score</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Monthly Trend */}
        <Card data-testid="chart-monthly-trend" className="bg-[#052e22]/50 border-emerald-800/30">
          <CardHeader className="border-b border-emerald-800/30 pb-4">
            <CardTitle className="text-xl font-semibold text-white">Monthly Trend</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#0a5c44" opacity={0.3} />
                <XAxis 
                  dataKey="month" 
                  stroke="#6ee7b7" 
                  tick={{ fill: '#6ee7b7' }}
                  axisLine={{ stroke: '#0a5c44' }}
                />
                <YAxis 
                  stroke="#6ee7b7" 
                  tick={{ fill: '#6ee7b7' }}
                  axisLine={{ stroke: '#0a5c44' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  formatter={(value) => <span className="text-emerald-300">{value}</span>}
                />
                <Line 
                  type="monotone" 
                  dataKey="leads" 
                  stroke="#10b981" 
                  strokeWidth={3} 
                  dot={{ fill: '#10b981', strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: '#10b981' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="generations" 
                  stroke="#6ee7b7" 
                  strokeWidth={3}
                  dot={{ fill: '#6ee7b7', strokeWidth: 2 }}
                  activeDot={{ r: 6, fill: '#6ee7b7' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Lead Status Distribution */}
        <Card data-testid="chart-lead-status" className="bg-[#052e22]/50 border-emerald-800/30">
          <CardHeader className="border-b border-emerald-800/30 pb-4">
            <CardTitle className="text-xl font-semibold text-white">Lead Status Distribution</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {leadStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={leadStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#10b981"
                    dataKey="value"
                    stroke="#021a13"
                    strokeWidth={2}
                  >
                    {leadStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#052e22', 
                      border: '1px solid #0a5c44',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-emerald-300/50">
                No lead status data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Performance Metrics Bar Chart */}
      <Card data-testid="chart-performance" className="bg-[#052e22]/50 border-emerald-800/30">
        <CardHeader className="border-b border-emerald-800/30 pb-4">
          <CardTitle className="text-xl font-semibold text-white">Performance Overview</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#0a5c44" opacity={0.3} />
              <XAxis 
                dataKey="month" 
                stroke="#6ee7b7"
                tick={{ fill: '#6ee7b7' }}
                axisLine={{ stroke: '#0a5c44' }}
              />
              <YAxis 
                stroke="#6ee7b7"
                tick={{ fill: '#6ee7b7' }}
                axisLine={{ stroke: '#0a5c44' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                formatter={(value) => <span className="text-emerald-300">{value}</span>}
              />
              <Bar 
                dataKey="leads" 
                fill="#10b981" 
                radius={[4, 4, 0, 0]}
                name="Leads"
              />
              <Bar 
                dataKey="generations" 
                fill="#0a5c44" 
                radius={[4, 4, 0, 0]}
                name="AI Generations"
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
